const M = 16;
const efConstruction = 200;
const MAX_LEVEL = 16;
function randomLevel() {
    let level = 0;
    while (Math.random() < 0.5 && level < MAX_LEVEL)
        level++;
    return level;
}
function cosineSimilarity(a, b) {
    let dot = 0;
    for (let i = 0; i < a.length; i++)
        dot += a[i] * b[i];
    return dot;
}
function getConnections(node, level) {
    return node.connections.get(level) ?? [];
}
function setConnections(node, level, connections) {
    node.connections.set(level, connections);
}
export function buildHNSW(vectors) {
    const ids = Array.from(vectors.keys());
    if (ids.length === 0) {
        return { nodes: [], entryPoint: -1, maxLevel: 0, M, efConstruction };
    }
    const nodes = ids.map((id, idx) => ({
        id,
        level: randomLevel(),
        connections: new Map(),
    }));
    const entryPoint = nodes[0];
    let maxLevel = entryPoint.level;
    for (const node of nodes) {
        if (node === entryPoint)
            continue;
        const vec = vectors.get(node.id);
        let currentBest = entryPoint.id;
        let currentBestScore = cosineSimilarity(vec, vectors.get(entryPoint.id));
        for (let level = maxLevel; level > node.level; level--) {
            let improved = true;
            while (improved) {
                improved = false;
                const neighbors = getConnections(nodes.find((n) => n.id === currentBest), level);
                for (const neighborId of neighbors) {
                    const score = cosineSimilarity(vec, vectors.get(neighborId));
                    if (score > currentBestScore) {
                        currentBest = neighborId;
                        currentBestScore = score;
                        improved = true;
                    }
                }
            }
        }
        for (let level = Math.min(node.level, maxLevel); level >= 0; level--) {
            const candidates = [];
            const neighborSet = new Set();
            const addNeighbor = (id) => {
                if (!neighborSet.has(id) && id !== node.id) {
                    neighborSet.add(id);
                    candidates.push({ id, score: cosineSimilarity(vec, vectors.get(id)) });
                }
            };
            addNeighbor(currentBest);
            const existingConns = getConnections(nodes.find((n) => n.id === currentBest), level);
            for (const id of existingConns)
                addNeighbor(id);
            candidates.sort((a, b) => b.score - a.score);
            const selected = candidates.slice(0, M).map((c) => c.id);
            setConnections(node, level, selected);
            for (const selectedId of selected) {
                const selectedNode = nodes.find((n) => n.id === selectedId);
                const conns = getConnections(selectedNode, level);
                if (conns.length < M) {
                    setConnections(selectedNode, level, [...conns, node.id]);
                }
                else {
                    const allCandidates = [
                        ...conns.map((id) => ({
                            id,
                            score: cosineSimilarity(vectors.get(selectedId), vectors.get(id)),
                        })),
                        { id: node.id, score: cosineSimilarity(vectors.get(selectedId), vec) },
                    ];
                    allCandidates.sort((a, b) => b.score - a.score);
                    setConnections(selectedNode, level, allCandidates.slice(0, M).map((c) => c.id));
                }
            }
            currentBest = candidates[0]?.id ?? currentBest;
        }
        if (node.level > maxLevel)
            maxLevel = node.level;
    }
    return { nodes, entryPoint: entryPoint.id, maxLevel, M, efConstruction };
}
export function hnswSearch(index, vectors, query, k = 10) {
    if (index.nodes.length === 0)
        return [];
    let currentBest = index.entryPoint;
    let currentBestScore = cosineSimilarity(query, vectors.get(currentBest));
    for (let level = index.maxLevel; level > 0; level--) {
        let improved = true;
        while (improved) {
            improved = false;
            const node = index.nodes.find((n) => n.id === currentBest);
            const neighbors = getConnections(node, level);
            for (const neighborId of neighbors) {
                const score = cosineSimilarity(query, vectors.get(neighborId));
                if (score > currentBestScore) {
                    currentBest = neighborId;
                    currentBestScore = score;
                    improved = true;
                }
            }
        }
    }
    const ef = Math.max(k * 2, 50);
    const candidates = [];
    const visited = new Set();
    const addCandidate = (id) => {
        if (!visited.has(id)) {
            visited.add(id);
            candidates.push({ id, score: cosineSimilarity(query, vectors.get(id)) });
        }
    };
    addCandidate(currentBest);
    const topNode = index.nodes.find((n) => n.id === currentBest);
    for (const id of getConnections(topNode, 0))
        addCandidate(id);
    candidates.sort((a, b) => b.score - a.score);
    const queue = candidates.slice(0, ef);
    while (queue.length > 0) {
        const current = queue.shift();
        if (candidates.length >= ef && current.score < candidates[candidates.length - 1].score)
            break;
        const node = index.nodes.find((n) => n.id === current.id);
        for (const neighborId of getConnections(node, 0)) {
            if (!visited.has(neighborId)) {
                visited.add(neighborId);
                const score = cosineSimilarity(query, vectors.get(neighborId));
                candidates.push({ id: neighborId, score });
                candidates.sort((a, b) => b.score - a.score);
                if (candidates.length > ef)
                    candidates.pop();
                queue.push({ id: neighborId, score });
            }
        }
    }
    return candidates.slice(0, k).sort((a, b) => b.score - a.score);
}
//# sourceMappingURL=build-vector-index.js.map